<script lang="ts" setup>
import {computed, ref} from "vue";
import LabeledTextInput from "@src/components/ui/inputs/LabeledTextInput.vue";
import PasswordInput from "@src/components/ui/inputs/PasswordInput.vue";
import Button from "@src/components/ui/inputs/Button.vue";
import useStore from "@src/stores/store";
import {useAuthStore} from "@src/stores/auth";
import {QuestionMarkCircleIcon} from "@heroicons/vue/24/outline";


const emit = defineEmits<{
    (e: "go-register"): void
}>();

const authStore = useAuthStore();
const store = useStore();

const mode = ref('login');
const transitionName = ref('slide-forward');

const email = ref("");
const password = ref("");
const isLoading = ref(false);
const errorMessage = computed(() => store.authError);
const successMessage = ref("");

const goToRecover = () => {
    transitionName.value = 'slide-forward';
    mode.value = 'recover';
    password.value = '';
    successMessage.value = '';
    store.clearAuthError();
};

const goToLogin = () => {
    transitionName.value = 'slide-back';
    mode.value = 'login';
    password.value = '';
    successMessage.value = '';
    store.clearAuthError();
};

const handleLogin = async () => {
    store.clearAuthError();

    if (!email.value || !password.value) {
        store.setAuthError("Por favor, preencha todos os campos.");
        return;
    }

    isLoading.value = true;

    try {
        await authStore.login({ email: email.value, password: password.value });

        store.setStatus("loading");
    } catch (error: any) {
        store.setStatus("idle");

        if (error?.code === "INVALID_CREDENTIALS" || error?.response?.status === 422) {
            store.setAuthError("E-mail ou senha incorretos.");
            return;
        }

        if (error?.response?.status === 419) {
            store.setAuthError("Sessão expirada. Recarregue a página.");
            return;
        }

        store.setAuthError("Ocorreu um erro ao tentar entrar.");
    } finally {
        isLoading.value = false;
    }
};

const handleRecover = async () => {
    store.clearAuthError();
    successMessage.value = '';

    if (!email.value) {
        store.setAuthError("Por favor, preencha o e-mail.");
        return;
    }

    isLoading.value = true;

    try {
        const result = await authStore.recover({ email: email.value });

        if (result.success) {
            successMessage.value = result.message;
        } else {
            store.setAuthError(result.message);
        }
    } catch (error: any) {
        if (error?.response?.status === 419) {
            store.setAuthError("Sessão expirada. Recarregue a página.");
            return;
        }

        if (error?.response?.status === 422 && error?.response?.data.message) {
            store.setAuthError(error?.response?.data.message);
            return;
        }

        store.setAuthError("Ocorreu um erro ao tentar recuperar a senha.");
    } finally {
        isLoading.value = false;
    }
};
</script>


<template>
    <div class="p-5 md:basis-1/2 xs:basis-full flex flex-col justify-center items-center overflow-hidden">
        <Transition :name="transitionName" mode="out-in">
            <div v-if="mode === 'login'" key="login" class="w-full md:px-[26%] xs:px-[10%]">

                <div class="mb-6 flex flex-col items-center justify-center">
                    <img
                        alt="dChat logo"
                        class="w-36 mb-4"
                        src="@src/assets/images/dchat_login_logo.png"
                    />
                    <p class="heading-2 text-black/70 dark:text-white/70 mb-4">
                        Bem-vindo de volta!
                    </p>
                    <p class="body-3 text-black/75 dark:text-white/70 font-light">
                        Entre com seu usuário e senha para continuar.
                    </p>
                </div>

                <form class="mb-6" @submit.prevent="handleLogin">
                    <LabeledTextInput
                        :value="email"
                        class="mb-5"
                        label="E-mail"
                        placeholder="Insira o seu e-mail"
                        type="email"
                        @value-changed="(value) => (email = value)"
                    />

                    <PasswordInput
                        :value="password"
                        class="mb-6"
                        label="Senha"
                        placeholder="Insira sua senha"
                        @value-changed="(value) => (password = value)"
                    />

                    <div v-if="errorMessage" class="mb-4 p-3 rounded bg-red-100 border border-red-200 text-red-700 text-sm">
                        {{ errorMessage }}
                    </div>

                    <div class="mb-6">
                        <Button
                            :disabled="isLoading"
                            :loading="isLoading"
                            class="group btn cursor-pointer contained-primary contained-text w-full mb-4"
                            type="submit"
                        >
                            <span v-if="!isLoading">Entrar</span>
                            <span v-else>Entrando...</span>
                        </Button>

                        <p class="body-1 text-black/75 dark:text-white/70 font-light text-center cursor-pointer underline" @click="goToRecover">
                          Esqueci minha senha
                        </p>

                      <!-- SUPORTE -->

                      <div class="flex flex-row justify-center">

                        <a
                            class="flex items-center body-1 text-black/75 dark:text-white/70 font-light cursor-pointer mt-4"
                            href="https://wa.link/ccz074"
                            rel="noopener noreferrer"
                            role="menuitem"
                            target="_blank"
                        >
                          <QuestionMarkCircleIcon class="h-5 w-5 mr-2" />
                          Falar com o Suporte
                        </a>
                      </div>

                    </div>
                </form>
            </div>

            <div v-else key="recover" class="w-full md:px-[26%] xs:px-[10%]">
                <div class="mb-6 flex flex-col items-center justify-center">
                    <img
                        alt="dChat logo"
                        class="w-36 mb-4"
                        src="@src/assets/images/dchat_login_logo.png"
                    />
                    <p class="heading-2 text-black/70 dark:text-white/70 mb-4">
                        Recuperar senha
                    </p>
                    <p class="body-3 text-black/75 dark:text-white/70 font-light">
                        Esqueceu sua senha? Sem problemas. É só nos informar o seu e-mail que nós enviaremos para você um link para redefinição de senha que irá permitir que você escolha uma nova senha.
                    </p>
                </div>
                <form class="mb-6" @submit.prevent="handleRecover()">
                    <LabeledTextInput
                        :value="email"
                        class="mb-5"
                        label="E-mail"
                        placeholder="Insira o seu e-mail"
                        type="email"
                        @value-changed="(value) => (email = value)"
                    />
                    <div v-if="errorMessage" class="mb-4 p-3 rounded bg-red-100 border border-red-200 text-red-700 text-sm">
                        {{ errorMessage }}
                    </div>

                    <div v-if="successMessage" class="mb-4 p-3 rounded bg-green-100 border border-green-200 text-green-700 text-sm">
                        {{ successMessage }}
                    </div>

                    <div class="mb-6">
                        <Button
                            v-if="!successMessage"
                            :disabled="isLoading"
                            :loading="isLoading"
                            class="group btn cursor-pointer contained-primary contained-text w-full mb-4"
                            type="submit"
                        >
                            <span v-if="!isLoading">Enviar</span>
                            <span v-else>Enviando...</span>
                        </Button>
                        <p class="body-1 text-black/75 dark:text-white/70 font-light text-center cursor-pointer underline" @click="goToLogin">Voltar para a tela de entrada</p>
                    </div>
                </form>
            </div>
        </Transition>
    </div>
</template>

<style scoped>
.slide-forward-enter-active,
.slide-forward-leave-active,
.slide-back-enter-active,
.slide-back-leave-active {
    transition: transform 0.3s ease, opacity 0.3s ease;
}

.slide-forward-enter-from {
    transform: translateX(30px);
    opacity: 0;
}
.slide-forward-leave-to {
    transform: translateX(-30px);
    opacity: 0;
}

.slide-back-enter-from {
    transform: translateX(-30px);
    opacity: 0;
}
.slide-back-leave-to {
    transform: translateX(30px);
    opacity: 0;
}
</style>
